import { Injectable, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { SHELL_CONFIG, ShellRuntimeConfig } from './shell-config';

/**
 * Loads runtime config (the API base URL) at app-init, exactly like the main
 * portal's ConfigService: tries `/assets/config.json` (pipeline-injected) and
 * falls back to the app-provided {@link SHELL_CONFIG} value.
 *
 * Generic, app-agnostic shell config service (extracted into `continity-shared-shell`,
 * PBI 1463 / ADO #1522). The hardcoded fallback is supplied per-app via SHELL_CONFIG,
 * so this service has no dependency on any app's `environment.ts`.
 */
@Injectable({ providedIn: 'root' })
export class ShellConfigService {
  private config: ShellRuntimeConfig | null = null;
  private configPromise: Promise<ShellRuntimeConfig> | null = null;

  constructor(
    private http: HttpClient,
    @Inject(SHELL_CONFIG) private fallback: ShellRuntimeConfig
  ) {}

  loadConfig(): Promise<ShellRuntimeConfig> {
    if (this.configPromise) return this.configPromise;

    this.configPromise = firstValueFrom(
      this.http.get<ShellRuntimeConfig>('/assets/config.json')
    )
      .then((config) => {
        if (!config.apiUrl || config.apiUrl === 'PLACEHOLDER_API_URL') {
          config.apiUrl = this.fallback.apiUrl;
        }
        if (!config.environment || config.environment === 'PLACEHOLDER_ENVIRONMENT') {
          config.environment = this.fallback.environment;
        }
        this.config = config;
        return config;
      })
      .catch(() => {
        const fallback: ShellRuntimeConfig = {
          apiUrl: this.fallback.apiUrl,
          environment: this.fallback.environment,
        };
        this.config = fallback;
        return fallback;
      });

    return this.configPromise;
  }

  get apiUrl(): string {
    return this.config?.apiUrl ?? this.fallback.apiUrl;
  }

  /**
   * Cross-portal links for the shell's product switcher (Option B — ADO #1543).
   * Prefers the loaded `config.json` value; falls back to the app-supplied
   * SHELL_CONFIG.portalLinks; defaults to an empty list (section then hidden).
   */
  get portalLinks(): { key: string; label: string; url: string }[] {
    return this.config?.portalLinks ?? this.fallback.portalLinks ?? [];
  }
}
